import ko from "knockout";
import protect from "flow/protect";
import * as _ from "underscore";
import MyFlow from "flow/MyFlow";
import environment from "flow/configuration/environment";
import PrefixedLogger from "flow/logging/PrefixedLogger";

class Listener {
    myFlow: MyFlow;
    callback: (data?: any) => void;
}

class TopicListener {
    topicId: string;
    callback: (data?: any) => void;
}

const listeners: { [id: string]: Listener[] } = {};
const webSocketTopics = ko.observableArray<string>();

const logger = new PrefixedLogger("[events]");

function logDebug(...params: any[]) {
    const debugEvents = environment.get(environment.keys.debugEvents);

    if (debugEvents) {
        logger.debug(...params);
    }
}

function publish(topicId: string, data?: any): void {
    logDebug(`publish event on topic ${topicId}`, data);

    if (!listeners[topicId]) {
        listeners[topicId] = [];
    }
    const listenersSnapshot = listeners[topicId].slice();
    listenersSnapshot.forEach(listener => {
        logDebug(`listener ${listener.myFlow.id} ${listener.myFlow.component.cuid} receives event`);
        protect(listener.myFlow, this, listener.callback)(data);
    });
}

function subscribe(myFlow: MyFlow, topicId: string, callback: (data?: any) => void, isGlobal?: boolean): void {
    logDebug(`subscribe ${myFlow.id} ${myFlow.component.cuid} to topic ${topicId}`);

    if (!listeners[topicId]) {
        listeners[topicId] = [{ myFlow: myFlow, callback: callback }];

        if (isGlobal === true && !webSocketTopics().includes(topicId)) {
            webSocketTopics.push(topicId);
        }
    } else {
        listeners[topicId].push({ myFlow: myFlow, callback: callback });
    }
}

function unsubscribe(topicId: string, callback: (data?: any) => void): void {
    if (!topicId) {
        logDebug(`unsubscribe missing topicId`);
        return;
    }

    if (!callback) {
        logDebug(`unsubscribe for topic ${topicId} missing callback`);
        return;
    }

    if (!listeners[topicId]) {
        logDebug(`unsubscribe for non-existent topic ${topicId}`);
        return;
    }

    const index = listeners[topicId].findIndex(listener => listener.callback === callback);

    if (index > -1) {
        const listener = listeners[topicId][index];
        logDebug(`unsubscribe ${listener.myFlow.id} ${listener.myFlow.component.cuid} from topic ${topicId}`);

        listeners[topicId].splice(index, 1);

        if (listeners[topicId].length === 0) {
            logDebug(`no listeners left, deleting topic ${topicId}`);
            webSocketTopics.remove(topicId);
            delete listeners[topicId];
        }
    } else {
        logDebug(`unsubscribe for topic ${topicId} but couldn't find listener`);
    }
}

function unsubscribeAllFor(myFlow: MyFlow): void {
    logDebug(`unsubscribe from all topics ${myFlow.id} ${myFlow.component.cuid}`);
    const listenersToBeRemoved: TopicListener[] = [];

    Object.entries(listeners).forEach(([topicId, topicListeners]) => {
        topicListeners
            .filter(listener => listener.myFlow === myFlow)
            .forEach(listener => {
                listenersToBeRemoved.push({
                    topicId: topicId,
                    callback: listener.callback
                });
            });
    });

    listenersToBeRemoved.forEach(listener => {
        unsubscribe(listener.topicId, listener.callback);
    });
}

export default {
    publish: publish,
    subscribe: subscribe,
    unsubscribe: unsubscribe,
    unsubscribeAllFor: unsubscribeAllFor,
    webSocketTopics: webSocketTopics
};
